import { useState } from 'react';
import { creatorbase } from '@round/api';
import {
    useBulkUploadListInfinite,
    useBulkUploadItems,
    useRetryBulkUploadItemMutation,
} from '@round/creatorbase-helpers/api-hooks';
import { Skeleton } from '@round/ui-kit';
import { Button, VirtualList } from '@round/whitelabel-ui';
import { formatDate, formatSnakeCaseToDisplay, showNotification } from 'helpers';
import { ExpandableHeader } from '../shared/ExpandableHeader';
import { PostUrlLink } from '../shared/PostUrlLink';
import styles from './BulkUploadReport.module.css';

type Props = {
    projectId: number | undefined;
    campaignId: number | undefined;
};

const UPLOADS_PAGE_SIZE = 2;

const BulkUploadReport = ({ projectId, campaignId }: Props) => {
    const {
        uploads,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isError,
        isFetchNextPageError,
        errorMessage,
    } = useBulkUploadListInfinite(
        { project_id: projectId ?? 0, campaign_id: campaignId, page_size: UPLOADS_PAGE_SIZE },
        { enabled: !!projectId && !!campaignId }
    );

    if (!projectId || !campaignId) {
        return <p className={styles.emptyState}>No project or campaign selected.</p>;
    }

    // Checked before isLoading/isError: once older uploads are showing, a failed "load more"
    // shouldn't yank away the ones already on screen.
    if (uploads.length) {
        return (
            <div className={styles.uploads}>
                {uploads.map((upload) => (
                    <UploadReport key={upload.id} upload={upload} />
                ))}
                {isFetchNextPageError && <p className={styles.errorMessage}>Could not load more uploads.</p>}
                {hasNextPage && (
                    <button
                        type="button"
                        className={styles.loadMoreButton}
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? 'Loading...' : 'Show older requests'}
                    </button>
                )}
            </div>
        );
    }

    if (isLoading) {
        return <Skeleton className={styles.summarySkeleton} />;
    }

    if (isError) {
        return <p className={styles.errorMessage}>{errorMessage || 'Could not load upload report'}</p>;
    }

    return <p className={styles.emptyState}>No bulk uploads yet for this campaign.</p>;
};

export default BulkUploadReport;

// Only used here (one entry per upload, one drill-in row per status): kept file-local rather
// than split out, per component-conventions.md's allowance for single-file helpers below the
// default export.
const ITEMS_PAGE_SIZE = 25;

type UploadReportProps = {
    upload: creatorbase.BulkUpload;
};

const UploadReport = ({ upload }: UploadReportProps) => (
    <div className={styles.container}>
        <dl className={styles.summary}>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Submitted</dt>
                <dd
                    className={`${styles.summaryValue} ${styles.summaryValueTruncate}`}
                    title={`${upload.user} · ${formatDate(upload.submitted_at)}`}
                >{`${upload.user} · ${formatDate(upload.submitted_at)}`}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Status</dt>
                <dd className={styles.summaryValue}>{formatSnakeCaseToDisplay(upload.status)}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Completed</dt>
                <dd className={styles.summaryValue}>
                    {upload.completed_at ? formatDate(upload.completed_at, { hour: '2-digit', minute: '2-digit' }) : '-'}
                </dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Requested</dt>
                <dd className={styles.summaryValue}>{upload.total_items}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Duplicated</dt>
                <dd className={styles.summaryValue}>{upload.duplicates}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Successful</dt>
                <dd className={styles.summaryValue}>{upload.successful}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Failed</dt>
                <dd className={styles.summaryValue}>{upload.failed}</dd>
            </div>
            <div className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>Pending</dt>
                <dd className={styles.summaryValue}>{upload.pending}</dd>
            </div>
        </dl>

        <div className={styles.groups}>
            {upload.pending > 0 && (
                <ItemGroup
                    uploadId={upload.id}
                    status="PENDING"
                    count={upload.pending}
                    label="pending"
                    className={styles.pendingGroup}
                />
            )}
            {upload.duplicates > 0 && (
                <ItemGroup
                    uploadId={upload.id}
                    status="DUPLICATE"
                    count={upload.duplicates}
                    label="duplicated"
                    className={styles.duplicateGroup}
                />
            )}
            {upload.invalid > 0 && (
                <ItemGroup
                    uploadId={upload.id}
                    status="INVALID"
                    count={upload.invalid}
                    label="invalid"
                    className={styles.invalidGroup}
                />
            )}
            {upload.failed > 0 && (
                <ItemGroup
                    uploadId={upload.id}
                    status="FAILED"
                    count={upload.failed}
                    label="failed"
                    className={styles.failedGroup}
                />
            )}
        </div>
    </div>
);

type ItemGroupProps = {
    uploadId: number;
    status: creatorbase.BulkUploadItemStatus;
    count: number;
    label: string;
    className?: string;
};

const ItemGroup = ({ uploadId, status, count, label, className }: ItemGroupProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        items,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isError,
        isFetchNextPageError,
        errorMessage,
    } = useBulkUploadItems(uploadId, { status, page_size: ITEMS_PAGE_SIZE }, { enabled: isExpanded });

    return (
        <div className={className}>
            <ExpandableHeader
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
                headerText={`${count} URL${count === 1 ? '' : 's'} ${label}`}
                className={styles.groupHeader}
            />
            {isExpanded &&
                (items.length ? (
                    <>
                        <VirtualList
                            className={styles.itemList}
                            data={items}
                            options={{ count: items.length, estimateSize: () => 40, gap: 2 }}
                            onEndReached={() => {
                                if (hasNextPage && !isFetchingNextPage) {
                                    fetchNextPage();
                                }
                            }}
                            isLoading={isFetchingNextPage}
                        >
                            {(item) => (
                                <div className={styles.item}>
                                    <div className={styles.itemRow}>
                                        <PostUrlLink url={item.url} className={styles.urlLink} />
                                        {item.attempt_count > 0 && (
                                            <span className={styles.itemAttempts}>
                                                {item.attempt_count} attempt{item.attempt_count === 1 ? '' : 's'}
                                            </span>
                                        )}
                                        {status === 'FAILED' && <RetryItemButton uploadId={uploadId} itemId={item.id} />}
                                    </div>
                                    {item.error_message && <p className={styles.itemError}>{item.error_message}</p>}
                                </div>
                            )}
                        </VirtualList>
                        {isFetchNextPageError && (
                            <p className={styles.statusMessage}>Could not load more items.</p>
                        )}
                    </>
                ) : isError ? (
                    <p className={styles.statusMessage}>{errorMessage || 'Could not load items'}</p>
                ) : isLoading ? (
                    <p className={styles.statusMessage}>Loading...</p>
                ) : (
                    <p className={styles.statusMessage}>No items found.</p>
                ))}
        </div>
    );
};

type RetryItemButtonProps = {
    uploadId: number;
    itemId: number;
};

const RetryItemButton = ({ uploadId, itemId }: RetryItemButtonProps) => {
    const { retry, isLoading } = useRetryBulkUploadItemMutation(uploadId, itemId);

    return (
        <Button
            className={styles.retryButton}
            appearance="ghost"
            onClick={(e) => {
                e.stopPropagation();
                retry(undefined, { onError: () => showNotification('Could not retry this URL', 'error') });
            }}
            disabled={isLoading}
            isLoading={isLoading}
            aria-label="Retry"
            hint="Retry"
        >
            Retry
        </Button>
    );
};
